import { ChangeEvent, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RadioGroup } from '@pwc/pwc-deals-ui';
import { useTranslation } from 'react-i18next';
import { NavigationNodeId } from '@pwc/pwc-deals-web-common';

import { getProject } from 'features/project/projectSlice';
import { setAnalysisFilterOption } from 'common/redux/userSettings/userSettingsSlice';
import {
  analysesCustomGroupsListPath,
  analysesKFAsGroupsListPath,
  analysesNoneGroupsListPath,
} from '../AnalysesGroupsContentRouter/AnalysesGroupsContentRouter';
import {
  GROUP_SETTING_KEY,
  saveUserPreferences,
} from '../../../../common/services/userPreferencesProjectService';
import {
  CUSTOM_GROUP_NAME,
  KFA_GROUP_NAME,
  NONE_GROUP_NAME,
} from '../../models/constants';

type GroupByKey = 'Custom' | 'Key Focus Area' | 'None';

type GroupByOption = {
  value: GroupByKey;
  label: string;
};

type RadioMenuProps = {
  defaultGroupBy?: GroupByKey;
};

export function RadioMenu({ defaultGroupBy = 'None' }: RadioMenuProps) {
  const history = useHistory();
  const [groupBy, setGroupBy] = useState(defaultGroupBy);
  const { id: projectId, navigation = [] } = useSelector(getProject);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  async function savePreferences(grouping: string) {
    return saveUserPreferences(projectId, GROUP_SETTING_KEY, grouping);
  }

  function isSectionHidden(id: NavigationNodeId) {
    return navigation.find((nav) => nav.id === id)?.isHidden;
  }

  function getSectionTitle(id: NavigationNodeId) {
    return navigation.find((nav) => nav.id === id)?.title;
  }

  function getOptions() {
    const options: GroupByOption[] = [];

    options.push({ value: 'Custom', label: t('groupBy.custom') });

    if (!isSectionHidden(NavigationNodeId.KEY_FOCUS_AREAS)) {
      const kfaTitle = getSectionTitle(NavigationNodeId.KEY_FOCUS_AREAS);
      options.push({
        value: 'Key Focus Area',
        label:
          kfaTitle !== undefined && kfaTitle !== 'Key Focus Area'
            ? kfaTitle
            : t('groupBy.KFA'),
      });
    }

    options.push({ value: 'None', label: t('groupBy.none') });

    return options;
  }

  const isSamePathname = (pathname: string) =>
    pathname === history.location.pathname;

  const groupActions = {
    Custom: {
      preference: CUSTOM_GROUP_NAME,
      route: analysesCustomGroupsListPath,
    },
    'Key Focus Area': {
      preference: KFA_GROUP_NAME,
      route: analysesKFAsGroupsListPath,
    },
    None: {
      preference: NONE_GROUP_NAME,
      route: analysesNoneGroupsListPath,
    },
  };

  const updateUserPreferences = async (groupByParam: GroupByKey) => {
    const action = groupActions[groupByParam];

    if (action && projectId) {
      const { preference, route } = action;

      await savePreferences(preference);

      const routePath = route(projectId);
      dispatch(setAnalysisFilterOption(groupByParam));

      if (!isSamePathname(routePath)) {
        history.push(routePath);
      }
    }
  };

  const onChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setGroupBy(e.target.value as GroupByKey);
    await updateUserPreferences(e.target.value as GroupByKey);
  };

  if (getOptions().length === 1) return null;

  return (
    <RadioGroup
      label={t('groupBy')}
      field={{
        name: 'groupBy',
        value: groupBy,
        onChange,
      }}
      options={getOptions()}
      direction="row"
    />
  );
}

export default RadioMenu;
